import React from 'react';
import { Card, Row, Col, Typography, Alert, List, Badge, Spin, Tooltip } from 'antd';
import { useAuth } from '../hooks/useAuth';
import { useDashboard } from '../hooks/useDashboard';
import { DASHBOARD_CONSTANTS } from '../constants/dashboard';
import dayjs from 'dayjs';

const { Title } = Typography;

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const {
    data,
    filteredData,
    soonMaintenances30Only,
    soonAudits30Only,
    loading,
    error
  } = useDashboard();

  // Render card thống kê công ty
  const renderCompanyStats = (company: any) => {
    const disposedCompany = data.companyDisposedStats.find((d: any) => d.company_id === company.company_id);
    const inUseCount = data.assetStatusByCompany.find((s: any) => s.status === 'in_use' && s.company_id === company.company_id)?.count || 0;
    return (
      <React.Fragment key={company.company_id}>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 1, color: '#1890ff' }}>
          {company.company_name}
        </div>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={6} key={company.company_id + '-total'}>
            <Card variant="outlined" style={DASHBOARD_CONSTANTS.STYLES.CARD}>
              <div style={{ ...DASHBOARD_CONSTANTS.STYLES.CARD_VALUE, color: DASHBOARD_CONSTANTS.STYLES.COLORS.SUCCESS }}>{company.total_assets}</div>
              <div style={DASHBOARD_CONSTANTS.STYLES.CARD_LABEL}>{DASHBOARD_CONSTANTS.LABELS.TOTAL_ASSETS}</div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6} key={company.company_id + '-value'}>
            <Card variant="outlined" style={DASHBOARD_CONSTANTS.STYLES.CARD}>
              <div style={{ ...DASHBOARD_CONSTANTS.STYLES.CARD_VALUE, color: '#722ed1' }}>{Math.round(company.total_value || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</div>
              <div style={DASHBOARD_CONSTANTS.STYLES.CARD_LABEL}>{DASHBOARD_CONSTANTS.LABELS.TOTAL_VALUE}</div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6} key={company.company_id + '-inuse'}>
            <Card variant="outlined" style={DASHBOARD_CONSTANTS.STYLES.CARD}>
              <div style={{ ...DASHBOARD_CONSTANTS.STYLES.CARD_VALUE, color: DASHBOARD_CONSTANTS.STYLES.COLORS.INFO }}>{inUseCount}</div>
              <div style={DASHBOARD_CONSTANTS.STYLES.CARD_LABEL}>{DASHBOARD_CONSTANTS.LABELS.IN_USE}</div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6} key={company.company_id + '-disposed'}>
            <Card variant="outlined" style={DASHBOARD_CONSTANTS.STYLES.CARD}>
              <div style={{ ...DASHBOARD_CONSTANTS.STYLES.CARD_VALUE, color: DASHBOARD_CONSTANTS.STYLES.COLORS.DISPOSED }}>{disposedCompany?.disposed_count || 0}</div>
              <div style={DASHBOARD_CONSTANTS.STYLES.CARD_LABEL}>{DASHBOARD_CONSTANTS.LABELS.DISPOSED}</div>
            </Card>
          </Col>
        </Row>
      </React.Fragment>
    );
  };

  // Render item trong list
  const renderListItem = (item: any, status: string, description: string, showTooltip: boolean = false) => {
    if (showTooltip) {
      // Cho maintenance: hiển thị mã tài sản với tooltip tên tài sản và ngày bảo dưỡng
      const assetCode = item.asset_code || `ID: ${item.asset_id}`;
      const assetName = item.asset_name || item.name || 'Không có tên';
      const maintenanceDate = item.next_due_date ? dayjs(item.next_due_date).format('DD/MM/YYYY') : '';

      return (
        <List.Item style={{ padding: '4px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Badge status={status as any} style={{ marginRight: 8, flexShrink: 0 }} />
            <Tooltip title={`Tên tài sản: ${assetName}`} placement="top">
              <span style={{
                cursor: 'help',
                color: '#1890ff',
                fontWeight: '500',
                display: 'inline-block',
                minWidth: 'fit-content',
                maxWidth: '100px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                marginRight: '8px'
              }}>
                {assetCode}
              </span>
            </Tooltip>
            <span style={{ color: '#666', fontSize: '13px', flex: 1 }}>
              - Ngày bảo dưỡng: {maintenanceDate}
            </span>
          </div>
        </List.Item>
      );
    } else {
      // Cho audit: giữ nguyên format hiện tại
      return (
        <List.Item>
          <Badge status={status as any} text={description || `${item.asset_name || item.name} (${item.asset_code || item.asset_id})`} />
        </List.Item>
      );
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Lỗi"
        description={error}
        type="error"
        showIcon
        style={{ margin: '20px' }}
      />
    );
  }

  return (
    <div>
      {/* Thống kê theo công ty - 3 row - Chỉ hiển thị cho super_admin và company_admin */}
      {data.companyAssetStats.length > 0 && (user?.role === 'super_admin' || user?.role === 'company_admin' || user?.role === 'manager') && (
        <>
          {data.companyAssetStats.slice(0, 3).map(renderCompanyStats)}
        </>
      )}

      {/* Thống kê cho Nhân viên (Employee) */}
      {user?.role === 'employee_user' && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Card variant="outlined" style={DASHBOARD_CONSTANTS.STYLES.CARD}>
              <div style={{ ...DASHBOARD_CONSTANTS.STYLES.CARD_VALUE, color: DASHBOARD_CONSTANTS.STYLES.COLORS.SUCCESS }}>{data.summary.total_assets}</div>
              <div style={DASHBOARD_CONSTANTS.STYLES.CARD_LABEL}>{DASHBOARD_CONSTANTS.LABELS.TOTAL_ASSETS}</div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card variant="outlined" style={DASHBOARD_CONSTANTS.STYLES.CARD}>
              <div style={{ ...DASHBOARD_CONSTANTS.STYLES.CARD_VALUE, color: '#722ed1' }}>{Math.round(data.summary.total_value || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</div>
              <div style={DASHBOARD_CONSTANTS.STYLES.CARD_LABEL}>{DASHBOARD_CONSTANTS.LABELS.TOTAL_VALUE}</div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card variant="outlined" style={DASHBOARD_CONSTANTS.STYLES.CARD}>
              <div style={{ ...DASHBOARD_CONSTANTS.STYLES.CARD_VALUE, color: DASHBOARD_CONSTANTS.STYLES.COLORS.INFO }}>{data.summary.in_use_count || 0}</div>
              <div style={DASHBOARD_CONSTANTS.STYLES.CARD_LABEL}>{DASHBOARD_CONSTANTS.LABELS.IN_USE}</div>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card variant="outlined" style={DASHBOARD_CONSTANTS.STYLES.CARD}>
              <div style={{ ...DASHBOARD_CONSTANTS.STYLES.CARD_VALUE, color: DASHBOARD_CONSTANTS.STYLES.COLORS.DISPOSED }}>{data.summary.disposed_count || 0}</div>
              <div style={DASHBOARD_CONSTANTS.STYLES.CARD_LABEL}>{DASHBOARD_CONSTANTS.LABELS.DISPOSED}</div>
            </Card>
          </Col>
        </Row>
      )}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} md={12}>
          <Alert
            message={
              filteredData.overdueMaintenances.length > 0
                ? DASHBOARD_CONSTANTS.MESSAGES.HAS_OVERDUE_MAINTENANCE(filteredData.overdueMaintenances.length)
                : DASHBOARD_CONSTANTS.MESSAGES.NO_OVERDUE_MAINTENANCE
            }
            type={filteredData.overdueMaintenances.length > 0 ? 'error' : 'success'}
            showIcon
            style={{ marginBottom: 8 }}
          />
          <List
            size="small"
            dataSource={filteredData.overdueMaintenances}
            style={{ minHeight: 114, maxHeight: 114, overflowY: 'auto', border: '1px solid #d9d9d9', borderRadius: 6, padding: '8px 12px' }}
            locale={{ emptyText: 'Không có lịch bảo dưỡng quá hạn' }}
            renderItem={item => renderListItem(item, 'error', '', true)}
          />
          <Alert
            message={
              filteredData.soonMaintenances7.length > 0
                ? DASHBOARD_CONSTANTS.MESSAGES.HAS_SOON_DUE_7_MAINTENANCE(filteredData.soonMaintenances7.length)
                : DASHBOARD_CONSTANTS.MESSAGES.NO_SOON_DUE_7_MAINTENANCE
            }
            type={filteredData.soonMaintenances7.length > 0 ? 'warning' : 'success'}
            showIcon
            style={{ margin: '16px 0 8px 0' }}
          />
          <List
            size="small"
            dataSource={filteredData.soonMaintenances7}
            style={{ minHeight: 114, maxHeight: 114, overflowY: 'auto', border: '1px solid #d9d9d9', borderRadius: 6, padding: '8px 12px' }}
            locale={{ emptyText: 'Không có lịch bảo dưỡng sắp tới hạn 7 ngày' }}
            renderItem={item => renderListItem(item, 'warning', '', true)}
          />
          <Alert
            message={
              soonMaintenances30Only.length > 0
                ? DASHBOARD_CONSTANTS.MESSAGES.HAS_SOON_DUE_30_MAINTENANCE(soonMaintenances30Only.length)
                : DASHBOARD_CONSTANTS.MESSAGES.NO_SOON_DUE_30_MAINTENANCE
            }
            type={soonMaintenances30Only.length > 0 ? 'info' : 'success'}
            showIcon
            style={{ margin: '16px 0 8px 0' }}
          />
          <List
            size="small"
            dataSource={soonMaintenances30Only}
            style={{ minHeight: 114, maxHeight: 114, overflowY: 'auto', border: '1px solid #d9d9d9', borderRadius: 6, padding: '8px 12px' }}
            locale={{ emptyText: 'Không có lịch bảo dưỡng sắp tới hạn 30 ngày' }}
            renderItem={item => renderListItem(item, 'processing', '', true)}
          />
        </Col>
        <Col xs={24} md={12}>
          <Alert
            message={
              filteredData.overdueAudits.length > 0
                ? DASHBOARD_CONSTANTS.MESSAGES.HAS_OVERDUE_AUDIT(filteredData.overdueAudits.length)
                : DASHBOARD_CONSTANTS.MESSAGES.NO_OVERDUE_AUDIT
            }
            type={filteredData.overdueAudits.length > 0 ? 'error' : 'success'}
            showIcon
            style={{ marginBottom: 8 }}
          />
          <List
            size="small"
            dataSource={filteredData.overdueAudits}
            style={{ minHeight: 114, maxHeight: 114, overflowY: 'auto', border: '1px solid #d9d9d9', borderRadius: 6, padding: '8px 12px' }}
            locale={{ emptyText: 'Không có tài sản kiểm kê quá hạn' }}
            renderItem={item => renderListItem(item, 'error', `${item.name} (${item.asset_code}) - Hạn kiểm kê: ${item.next_audit_due ? dayjs(item.next_audit_due).format('DD/MM/YYYY') : ''}`, false)}
          />
          <Alert
            message={
              filteredData.soonAudits7.length > 0
                ? DASHBOARD_CONSTANTS.MESSAGES.HAS_SOON_AUDIT_7(filteredData.soonAudits7.length)
                : DASHBOARD_CONSTANTS.MESSAGES.NO_SOON_AUDIT_7
            }
            type={filteredData.soonAudits7.length > 0 ? 'warning' : 'success'}
            showIcon
            style={{ margin: '16px 0 8px 0' }}
          />
          <List
            size="small"
            dataSource={filteredData.soonAudits7}
            style={{ minHeight: 114, maxHeight: 114, overflowY: 'auto', border: '1px solid #d9d9d9', borderRadius: 6, padding: '8px 12px' }}
            locale={{ emptyText: 'Không có tài sản kiểm kê sắp tới hạn 7 ngày' }}
            renderItem={item => renderListItem(item, 'warning', `${item.name} (${item.asset_code}) - Hạn kiểm kê: ${item.next_audit_due ? dayjs(item.next_audit_due).format('DD/MM/YYYY') : ''}`, false)}
          />
          <Alert
            message={
              soonAudits30Only.length > 0
                ? DASHBOARD_CONSTANTS.MESSAGES.HAS_SOON_AUDIT_30(soonAudits30Only.length)
                : DASHBOARD_CONSTANTS.MESSAGES.NO_SOON_AUDIT_30
            }
            type={soonAudits30Only.length > 0 ? 'info' : 'success'}
            showIcon
            style={{ margin: '16px 0 8px 0' }}
          />
          <List
            size="small"
            dataSource={soonAudits30Only}
            style={{ minHeight: 114, maxHeight: 114, overflowY: 'auto', border: '1px solid #d9d9d9', borderRadius: 6, padding: '8px 12px' }}
            locale={{ emptyText: 'Không có tài sản kiểm kê sắp tới hạn 30 ngày' }}
            renderItem={item => renderListItem(item, 'processing', `${item.name} (${item.asset_code}) - Hạn kiểm kê: ${item.next_audit_due ? dayjs(item.next_audit_due).format('DD/MM/YYYY') : ''}`, false)}
          />
        </Col>
      </Row>
    </div>
  );
};

export default DashboardPage; 